/**
 * Validador de DNI/NIE español para frontend
 * Sin dependencias externas
 *
 * Mismo criterio que `apps/backend/src/common/utils/dni.util.ts` (mismos casos de test).
 * El resultado lleva un **código** (`EMPTY`, `FORMAT`, `LETTER`) y la letra esperada: el
 * texto lo pone i18n en quien lo muestra. `error` (en catalán) queda solo por compatibilidad.
 */

import {
  checkSpanishDocument,
  mossosDocumentTypeCode,
  normalizeSpanishDocumentNumber,
} from '@guilleumes/types';

const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

const DNI_FORMAT = /^\d{8}[A-Z]$/;
const NIE_FORMAT = /^[XYZ]\d{7}[A-Z]$/;

export type SpanishDocumentType = 'D' | 'N';
export type DocumentValidationCode = 'EMPTY' | 'FORMAT' | 'LETTER';

export interface DNIValidationResult {
  isValid: boolean;
  type: 'DNI' | 'NIE' | 'UNKNOWN';
  /** Solo si `isValid` es false */
  code?: DocumentValidationCode;
  /** @deprecated texto en catalán; mostrar `code` traducido */
  error?: string;
  expectedLetter?: string;
}

/** Quita espacios, guiones y puntos, y pasa a mayúsculas */
export function normalizeDocumentNumber(document: string | null | undefined): string {
  return normalizeSpanishDocumentNumber(document);
}

/**
 * Tipo que dice el **formato** del número: `^\d{8}[A-Z]$` → DNI (`D`),
 * `^[XYZ]\d{7}[A-Z]$` → NIE (`N`); cualquier otra cosa → `null` (documento extranjero,
 * pasaporte, o un DNI mal escrito). No mira la letra de control.
 */
export function inferSpanishDocumentType(
  document: string | null | undefined,
): SpanishDocumentType | null {
  const normalized = normalizeDocumentNumber(document);
  if (NIE_FORMAT.test(normalized)) return 'N';
  if (DNI_FORMAT.test(normalized)) return 'D';
  return null;
}

/**
 * Normaliza el tipo de documento a los códigos de Mossos (`D`, `N`, `P`, `O`).
 * Mismos alias que `mapDocumentTypeV24` del backend; vacío o desconocido → `''`.
 */
export function normalizeDocumentType(type: string | null | undefined): 'D' | 'N' | 'P' | 'O' | '' {
  return mossosDocumentTypeCode(type);
}

/**
 * Valida un DNI/NIE español
 */
export function validateSpanishDocument(document: string): DNIValidationResult {
  // La regla (formato y letra de control) es la de `@guilleumes/types`; aquí solo se le pone el
  // texto en catalán que algunos sitios todavía muestran.
  const check = checkSpanishDocument(document);
  if (check.isValid) return { isValid: true, type: check.kind };

  if (check.issue === 'EMPTY') {
    return { isValid: false, type: 'UNKNOWN', code: 'EMPTY', error: 'Document buit' };
  }
  if (check.issue === 'LETTER') {
    return {
      isValid: false,
      type: check.kind,
      code: 'LETTER',
      error: `Lletra incorrecta. Esperada: ${check.expectedLetter}`,
      expectedLetter: check.expectedLetter,
    };
  }
  const formatError: Record<typeof check.kind, string> = {
    DNI: 'Format invàlid. Ha de ser 8 dígits + 1 lletra',
    NIE: 'Format invàlid. Ha de ser X/Y/Z + 7 dígits + 1 lletra',
    UNKNOWN: 'Format no reconegut',
  };
  return { isValid: false, type: check.kind, code: 'FORMAT', error: formatError[check.kind] };
}

/**
 * Calcula la letra correcta para un DNI
 */
export function calculateDNILetter(number: string): string {
  const cleanNumber = number.replace(/\D/g, '');
  return DNI_LETTERS[parseInt(cleanNumber, 10) % 23];
}

/**
 * Verifica si es un documento español válido (solo booleano)
 */
export function isValidSpanishDocument(document: string): boolean {
  return validateSpanishDocument(document).isValid;
}
